/**
 * Components for rendering call and SMS items in conversation
 */

import React from 'react';
import { IonIcon, IonButton } from '@ionic/react';
import { call, chatbubble, play } from 'ionicons/icons';
import styles from './CallSmsMessageItems.module.scss';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';

export interface CallItemProps {
  _callId: string;
  direction: 'inbound' | 'outbound';
  duration: number;
  callerName?: string;
  customerNumber: string;
  recordingUrl?: string;
  timestamp: Date;
  onPlayRecording?: (recordingUrl: string) => void;
}

/**
 * Render a call as a message-like item
 */
export const CallMessageItem: React.FC<CallItemProps> = ({
  _callId,
  direction,
  duration,
  callerName,
  customerNumber,
  recordingUrl,
  timestamp,
  onPlayRecording,
}) => {
  const { t } = useTranslation();
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const displayName = callerName || customerNumber || t('components.CallSmsMessageItems.unknownCaller');
  const isInbound = direction === 'inbound';
  const directionLabel = isInbound ? t('components.CallSmsMessageItems.incoming') : t('components.CallSmsMessageItems.outgoing');

  return (
    <div className={`${styles.messageItemCall} ${isInbound ? styles.inbound : styles.outbound}`}>
      <div className={styles.avatar}>
        <IonIcon icon={call} />
      </div>

      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <div className={styles.cardTitle}>{t('components.CallSmsMessageItems.call', { directionLabel })}</div>
              <div className={styles.cardSubtitle}>{displayName}</div>
              <div className={styles.cardMeta}>{t('components.CallSmsMessageItems.duration')} {formatDuration(duration)}</div>
            </div>
            {recordingUrl && (
              <IonButton
                fill="clear"
                size="small"
                onClick={() => onPlayRecording?.(recordingUrl)}
              >
                <IonIcon icon={play} slot="start" />{t('components.CallSmsMessageItems.play')}
              </IonButton>
            )}
          </div>
        </div>
        <div className={styles.timestamp}>{timestamp.toLocaleString()}</div>
      </div>
    </div>
  );
};

export interface SmsItemProps {
  messageText: string;
  direction: 'inbound' | 'outbound';
  _customerNumber: string;
  timestamp: Date;
}

/**
 * Render an SMS as a message-like item
 */
export const SmsMessageItem: React.FC<SmsItemProps> = ({
  messageText,
  direction,
  _customerNumber,
  timestamp,
}) => {
  const isInbound = direction === 'inbound';

  return (
    <div className={`${styles.messageItemSms} ${isInbound ? styles.inbound : styles.outbound}`}>
      {isInbound && (
        <div className={styles.avatar}>
          <IonIcon icon={chatbubble} />
        </div>
      )}

      <div className={styles.content}>
        <div className={`${styles.card} ${isInbound ? styles.inbound : styles.outbound}`}>
          <div className={styles.text}>{messageText}</div>
        </div>
        <div className={`${styles.timestamp} ${isInbound ? styles.inbound : styles.outbound}`}>
          {timestamp.toLocaleString()}
        </div>
      </div>

      {!isInbound && (
        <div className={styles.avatar}>
          <IonIcon icon={chatbubble} />
        </div>
      )}
    </div>
  );
};

export interface SystemMessageProps {
  message: string;
  timestamp: Date;
}

/**
 * Render a system message (e.g., "Call started", "SMS received")
 */
export const SystemMessageItem: React.FC<SystemMessageProps> = ({
  message,
  timestamp,
}) => {
  return (
    <div className={styles.systemMessage}>
      <div className={styles.content}>
        {message}
        <div className={styles.timestamp}>{timestamp.toLocaleTimeString(intlLocale())}</div>
      </div>
    </div>
  );
};
